import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Typography, Button, Paper, Chip, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Tooltip, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Alert, Grid, CircularProgress,
  List, ListItem, ListItemIcon, ListItemText,
  FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import {
  Add as AddIcon, Refresh as RefreshIcon,
  Delete as DeleteIcon, HealthAndSafety as HealthIcon,
  RocketLaunch as ProvisionIcon, Storage as StorageIcon,
  CheckCircle as OkIcon, Error as FailIcon, Warning as WarnIcon,
} from '@mui/icons-material';
import {
  fetchDeliveryNodes, createDeliveryNode, provisionDeliveryNode,
  checkDeliveryNodeHealth,
} from '../store/slices/deliverySlice';

function DeliveryNodes() {
  const dispatch = useDispatch();
  const { nodes, error } = useSelector((state) => state.delivery);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [provisionResult, setProvisionResult] = useState(null);
  const [provisioning, setProvisioning] = useState(null);
  const [provisionConfig, setProvisionConfig] = useState({ smb_username: '', smb_password: '' });
  const [formData, setFormData] = useState({
    name: '', hostname: '', ip_address: '',
    network_interfaces: [
      { name: 'ens18', role: 'management', mode: 'dhcp', address: '' },
      { name: 'ens19', role: 'storage', mode: 'static', address: '' },
    ],
    nas_ip: '192.168.50.10', nas_share: 'Screeners', nas_mountpoint: '/mnt/Screeners',
    minio_port: 9000, minio_console_port: 9001,
    minio_access_key: '', minio_secret_key: '',
    minio_bucket: 'deliveries', minio_bind_ip: '0.0.0.0', minio_data_path: '/srv/minio-data',
    ssh_user: 'it', ssh_port: 22, ssh_key_path: '/opt/watermarked-screeners/.ssh/dcp_encoder',
    public_url: 'https://deliver.hollywoodhills.media',
  });

  useEffect(() => { dispatch(fetchDeliveryNodes()); }, [dispatch]);

  const handleAdd = async () => {
    try {
      await dispatch(createDeliveryNode(formData)).unwrap();
      setAddDialogOpen(false);
    } catch (_err) { /* redux */ }
  };

  const [provisionDialog, setProvisionDialog] = useState(null);

  const handleProvision = async () => {
    if (!provisionDialog) return;
    setProvisioning(provisionDialog.id);
    setProvisionResult(null);
    setProvisionDialog(null);
    try {
      const result = await dispatch(provisionDeliveryNode({ nodeId: provisionDialog.id, config: provisionConfig })).unwrap();
      setProvisionResult(result);
      dispatch(fetchDeliveryNodes());
    } catch (err) {
      setProvisionResult({ overall: 'failed', message: typeof err === 'string' ? err : 'Provision failed', steps: [] });
    } finally {
      setProvisioning(null);
    }
  };

  const stepIcon = (s) => {
    if (s === 'ok') return <OkIcon fontSize="small" color="success" />;
    if (s === 'warning') return <WarnIcon fontSize="small" color="warning" />;
    return <FailIcon fontSize="small" color="error" />;
  };

  const formatDisk = (gb) => gb ? (gb > 1000 ? `${(gb / 1000).toFixed(1)} TB` : `${gb.toFixed(0)} GB`) : '—';

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>Delivery Nodes</Typography>
          <Typography variant="body2" color="text.secondary">MinIO object storage servers for file delivery</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh"><IconButton onClick={() => dispatch(fetchDeliveryNodes())}><RefreshIcon /></IconButton></Tooltip>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddDialogOpen(true)}>Add Node</Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
        <Table sx={{ minWidth: 1000 }}>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>IP / Ports</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Bucket</TableCell>
              <TableCell>Disk Free</TableCell>
              <TableCell>Public URL</TableCell>
              <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {nodes.filter(n => n.is_active).map(node => (
              <TableRow key={node.id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <StorageIcon fontSize="small" color="action" />
                    <Typography variant="body2" fontWeight={500}>{node.name}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{node.ip_address}:{node.minio_port}</Typography>
                </TableCell>
                <TableCell>
                  <Chip label={node.is_online ? 'Online' : 'Offline'} color={node.is_online ? 'success' : 'default'} size="small" />
                </TableCell>
                <TableCell>{node.minio_bucket}</TableCell>
                <TableCell>{formatDisk(node.disk_free_gb)}</TableCell>
                <TableCell>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{node.public_url || '—'}</Typography>
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  <Tooltip title="Provision (fresh install)">
                    <IconButton size="small" onClick={() => setProvisionDialog(node)} disabled={provisioning === node.id}>
                      {provisioning === node.id ? <CircularProgress size={18} /> : <ProvisionIcon fontSize="small" color="primary" />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Check Health">
                    <IconButton size="small" onClick={() => dispatch(checkDeliveryNodeHealth(node.id))}>
                      <HealthIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {nodes.filter(n => n.is_active).length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No delivery nodes configured. Add one to enable file delivery.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add Delivery Node</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography variant="subtitle2">Identity</Typography>
            <Grid container spacing={2}>
              <Grid size={4}><TextField label="Name" value={formData.name} onChange={(e) => setFormData(f => ({ ...f, name: e.target.value }))} fullWidth required /></Grid>
              <Grid size={4}><TextField label="Hostname" value={formData.hostname} onChange={(e) => setFormData(f => ({ ...f, hostname: e.target.value }))} fullWidth /></Grid>
              <Grid size={4}><TextField label="Management IP" value={formData.ip_address} onChange={(e) => setFormData(f => ({ ...f, ip_address: e.target.value }))} fullWidth required helperText="SSH access IP" /></Grid>
            </Grid>

            <Typography variant="subtitle2">Network Interfaces</Typography>
            <Typography variant="caption" color="text.secondary">Configure each NIC with a role. Management = SSH/API. Storage = NAS traffic. Public = download traffic.</Typography>
            {formData.network_interfaces.map((iface, i) => (
              <Grid container spacing={1} key={i} alignItems="center">
                <Grid size={2}><TextField label="NIC" size="small" value={iface.name} onChange={(e) => { const nifs = [...formData.network_interfaces]; nifs[i] = { ...nifs[i], name: e.target.value }; setFormData(f => ({ ...f, network_interfaces: nifs })); }} fullWidth /></Grid>
                <Grid size={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Role</InputLabel>
                    <Select value={iface.role} label="Role" onChange={(e) => { const nifs = [...formData.network_interfaces]; nifs[i] = { ...nifs[i], role: e.target.value }; setFormData(f => ({ ...f, network_interfaces: nifs })); }}>
                      <MenuItem value="management">Management</MenuItem>
                      <MenuItem value="storage">Storage</MenuItem>
                      <MenuItem value="public">Public</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Mode</InputLabel>
                    <Select value={iface.mode} label="Mode" onChange={(e) => { const nifs = [...formData.network_interfaces]; nifs[i] = { ...nifs[i], mode: e.target.value }; setFormData(f => ({ ...f, network_interfaces: nifs })); }}>
                      <MenuItem value="dhcp">DHCP</MenuItem>
                      <MenuItem value="static">Static</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={4}><TextField label="Address (CIDR)" size="small" value={iface.address} onChange={(e) => { const nifs = [...formData.network_interfaces]; nifs[i] = { ...nifs[i], address: e.target.value }; setFormData(f => ({ ...f, network_interfaces: nifs })); }} fullWidth placeholder="192.168.50.x/24" disabled={iface.mode === 'dhcp'} /></Grid>
                <Grid size={2}>
                  <IconButton size="small" onClick={() => setFormData(f => ({ ...f, network_interfaces: f.network_interfaces.filter((_, j) => j !== i) }))}><DeleteIcon fontSize="small" /></IconButton>
                </Grid>
              </Grid>
            ))}
            <Button size="small" onClick={() => setFormData(f => ({ ...f, network_interfaces: [...f.network_interfaces, { name: '', role: 'public', mode: 'static', address: '' }] }))}>
              + Add Interface
            </Button>

            <Typography variant="subtitle2">NAS Connection</Typography>
            <Grid container spacing={2}>
              <Grid size={4}><TextField label="NAS IP" size="small" value={formData.nas_ip} onChange={(e) => setFormData(f => ({ ...f, nas_ip: e.target.value }))} fullWidth helperText="Reached via storage NIC" /></Grid>
              <Grid size={4}><TextField label="Share Name" size="small" value={formData.nas_share} onChange={(e) => setFormData(f => ({ ...f, nas_share: e.target.value }))} fullWidth /></Grid>
              <Grid size={4}><TextField label="Mount Point" size="small" value={formData.nas_mountpoint} onChange={(e) => setFormData(f => ({ ...f, nas_mountpoint: e.target.value }))} fullWidth /></Grid>
            </Grid>

            <Typography variant="subtitle2">MinIO</Typography>
            <Grid container spacing={2}>
              <Grid size={3}><TextField label="Port" type="number" size="small" value={formData.minio_port} onChange={(e) => setFormData(f => ({ ...f, minio_port: parseInt(e.target.value) }))} fullWidth /></Grid>
              <Grid size={3}><TextField label="Console Port" type="number" size="small" value={formData.minio_console_port} onChange={(e) => setFormData(f => ({ ...f, minio_console_port: parseInt(e.target.value) }))} fullWidth /></Grid>
              <Grid size={3}><TextField label="Bind IP" size="small" value={formData.minio_bind_ip} onChange={(e) => setFormData(f => ({ ...f, minio_bind_ip: e.target.value }))} fullWidth helperText="0.0.0.0 = all" /></Grid>
              <Grid size={3}><TextField label="Bucket" size="small" value={formData.minio_bucket} onChange={(e) => setFormData(f => ({ ...f, minio_bucket: e.target.value }))} fullWidth /></Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid size={4}><TextField label="Access Key" size="small" value={formData.minio_access_key} onChange={(e) => setFormData(f => ({ ...f, minio_access_key: e.target.value }))} fullWidth /></Grid>
              <Grid size={4}><TextField label="Secret Key" type="password" size="small" value={formData.minio_secret_key} onChange={(e) => setFormData(f => ({ ...f, minio_secret_key: e.target.value }))} fullWidth /></Grid>
              <Grid size={4}><TextField label="Data Path" size="small" value={formData.minio_data_path} onChange={(e) => setFormData(f => ({ ...f, minio_data_path: e.target.value }))} fullWidth /></Grid>
            </Grid>

            <TextField label="Public URL" value={formData.public_url} onChange={(e) => setFormData(f => ({ ...f, public_url: e.target.value }))} fullWidth placeholder="https://deliver.hollywoodhills.media" />
            <TextField label="SSH Key Path" size="small" value={formData.ssh_key_path} onChange={(e) => setFormData(f => ({ ...f, ssh_key_path: e.target.value }))} fullWidth />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAdd} disabled={!formData.name || !formData.ip_address}>Add Node</Button>
        </DialogActions>
      </Dialog>

      {/* Provision dialog — asks for SMB credentials */}
      <Dialog open={Boolean(provisionDialog)} onClose={() => setProvisionDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Provision: {provisionDialog?.name}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This will configure networking, mount NAS, install MinIO + nginx, and tune the kernel.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="SMB Username" value={provisionConfig.smb_username}
              onChange={(e) => setProvisionConfig(c => ({ ...c, smb_username: e.target.value }))} fullWidth />
            <TextField label="SMB Password" type="password" value={provisionConfig.smb_password}
              onChange={(e) => setProvisionConfig(c => ({ ...c, smb_password: e.target.value }))} fullWidth />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setProvisionDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleProvision}>Provision</Button>
        </DialogActions>
      </Dialog>

      {/* Provision results */}
      <Dialog open={Boolean(provisionResult)} onClose={() => setProvisionResult(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Provision Results
          {provisionResult?.overall === 'ok'
            ? <Chip label="Passed" color="success" size="small" sx={{ ml: 1 }} />
            : <Chip label="Issues" color="error" size="small" sx={{ ml: 1 }} />
          }
        </DialogTitle>
        <DialogContent>
          {provisionResult?.message && <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{provisionResult.message}</Typography>}
          <List dense>
            {(provisionResult?.steps || []).map((step, i) => (
              <ListItem key={i}>
                <ListItemIcon sx={{ minWidth: 36 }}>{stepIcon(step.status)}</ListItemIcon>
                <ListItemText primary={step.step} secondary={step.error || step.version || step.bucket || null} />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions><Button onClick={() => setProvisionResult(null)}>Close</Button></DialogActions>
      </Dialog>
    </Box>
  );
}

export default DeliveryNodes;
